import { useCallback, useEffect, useMemo, useState } from "react";
import { CalendarX2, Plus, Save, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ConfirmDelete } from "@/components/admin/shared";

const DAYS = [["mon", "Monday"], ["tue", "Tuesday"], ["wed", "Wednesday"], ["thu", "Thursday"], ["fri", "Friday"], ["sat", "Saturday"], ["sun", "Sunday"]];
const input = "rounded-lg border border-white/10 bg-[#07090E] px-3 py-2 text-sm text-white outline-none focus:border-cyan-400/60";
const card = "rounded-2xl border border-white/5 bg-[#0B0F18] p-5";
const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail));

const HoursEditor = ({ hours, onChange }) => (
  <div className="space-y-2">
    {DAYS.map(([d, label]) => {
      const win = hours[d]?.[0];
      const set = (w) => onChange({ ...hours, [d]: w ? [w] : [] });
      return (
        <div key={d} data-testid={`hours-row-${d}`} className="flex flex-wrap items-center gap-3 text-sm">
          <label className="flex w-32 items-center gap-2 text-slate-200">
            <input type="checkbox" checked={!!win} onChange={(e) => set(e.target.checked ? ["09:00", "17:00"] : null)} data-testid={`hours-open-${d}`} className="h-4 w-4 accent-cyan-400" />{label}
          </label>
          {win ? (
            <>
              <input type="time" step="300" value={win[0]} onChange={(e) => set([e.target.value, win[1]])} data-testid={`hours-from-${d}`} className={input} />
              <span className="text-slate-500">to</span>
              <input type="time" step="300" value={win[1]} onChange={(e) => set([win[0], e.target.value])} data-testid={`hours-to-${d}`} className={input} />
            </>
          ) : <span className="text-slate-500">Closed</span>}
        </div>
      );
    })}
  </div>
);

const ServicesEditor = ({ appId, services, reload }) => {
  const [form, setForm] = useState({ name: "", duration: 60, price: "" });
  const [deleting, setDeleting] = useState(null);
  const base = `/manage/apps/${appId}/booking/services`;
  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post(base, { name: form.name, duration: Number(form.duration), price: form.price === "" ? null : Number(form.price) });
      toast.success("Service added"); setForm({ name: "", duration: 60, price: "" }); reload();
    } catch (x) { err(x); }
  };
  const toggle = async (s) => { try { await api.patch(`${base}/${s.id}`, { active: !s.active }); reload(); } catch (x) { err(x); } };
  const remove = async () => { try { await api.delete(`${base}/${deleting.id}`); setDeleting(null); toast.success("Service deleted"); reload(); } catch (x) { err(x); } };
  return (
    <div className={card}>
      <h3 className="mb-3 font-semibold text-white">Services</h3>
      <ul className="mb-4 divide-y divide-white/5">
        {services.map((s) => (
          <li key={s.id} data-testid={`service-row-${s.id}`} className="flex items-center gap-3 py-2 text-sm text-slate-300">
            <span className="flex-1 text-white">{s.name}</span><span>{s.duration_label}</span><span className="w-20 text-right">{s.price_label || "—"}</span>
            <button onClick={() => toggle(s)} data-testid={`service-toggle-${s.id}`} className="rounded-full border border-white/10 px-3 py-1 text-xs hover:border-cyan-400/50">{s.active ? "Hide" : "Show"}</button>
            <button onClick={() => setDeleting(s)} aria-label="Delete service" data-testid={`service-delete-${s.id}`} className="p-1 text-slate-400 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
          </li>
        ))}
        {!services.length && <li data-testid="services-empty" className="py-3 text-sm text-slate-500">No services yet — add one so visitors can book.</li>}
      </ul>
      <form onSubmit={add} className="flex flex-wrap gap-2">
        <input required placeholder="Service name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="service-name-input" className={`${input} flex-1`} />
        <input required type="number" min="5" max="480" step="5" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} aria-label="Minutes" data-testid="service-duration-input" className={`${input} w-24`} />
        <input type="number" min="0" step="0.01" placeholder="Price (info)" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="service-price-input" className={`${input} w-32`} />
        <button type="submit" data-testid="service-add-btn" className="inline-flex items-center gap-1 rounded-full bg-cyan-400 px-4 py-2 text-sm font-semibold text-[#07090E] hover:bg-cyan-300"><Plus className="h-4 w-4" />Add</button>
      </form>
      <ConfirmDelete target={deleting} label={deleting ? `service ${deleting.name}` : ""} onCancel={() => setDeleting(null)} onConfirm={remove} />
    </div>
  );
};

export const AvailabilityPanel = ({ appId }) => {
  const [data, setData] = useState(null);
  const [st, setSt] = useState(null);
  const [blocked, setBlocked] = useState("");
  const zones = useMemo(() => (Intl.supportedValuesOf ? Intl.supportedValuesOf("timeZone") : ["America/New_York"]), []);
  const load = useCallback(() => api.get(`/manage/apps/${appId}/booking`).then((r) => { setData(r.data); setSt(r.data.settings); }).catch(err), [appId]);
  useEffect(() => { load(); }, [load]);
  if (!st) return <p className="text-slate-500">Loading…</p>;
  const save = async () => {
    try { await api.put(`/manage/apps/${appId}/booking/settings`, st); toast.success("Availability saved"); load(); } catch (x) { err(x); }
  };
  const addBlocked = () => { if (blocked && !st.blocked_dates.includes(blocked)) setSt({ ...st, blocked_dates: [...st.blocked_dates, blocked].sort() }); setBlocked(""); };
  return (
    <div data-testid="availability-panel" className="grid gap-5 lg:grid-cols-2">
      <div className={card}>
        <h3 className="mb-3 font-semibold text-white">Weekly hours</h3>
        <label className="mb-4 flex flex-wrap items-center gap-2 text-sm text-slate-300">Timezone
          <select value={st.timezone} onChange={(e) => setSt({ ...st, timezone: e.target.value })} data-testid="booking-timezone-select" className={input}>
            {zones.map((z) => <option key={z} value={z}>{z}</option>)}
          </select>
        </label>
        <HoursEditor hours={st.hours} onChange={(hours) => setSt({ ...st, hours })} />
        <h3 className="mb-2 mt-6 font-semibold text-white">Blocked dates</h3>
        <div className="mb-3 flex flex-wrap gap-2">
          {st.blocked_dates.map((d) => (
            <span key={d} data-testid={`blocked-date-${d}`} className="inline-flex items-center gap-1 rounded-full border border-amber-400/30 bg-amber-400/10 px-3 py-1 text-xs text-amber-200">
              {d}<button onClick={() => setSt({ ...st, blocked_dates: st.blocked_dates.filter((x) => x !== d) })} aria-label={`Unblock ${d}`} data-testid={`blocked-date-remove-${d}`}><X className="h-3 w-3" /></button>
            </span>
          ))}
          {!st.blocked_dates.length && <span className="text-sm text-slate-500">None</span>}
        </div>
        <div className="flex gap-2">
          <input type="date" value={blocked} onChange={(e) => setBlocked(e.target.value)} data-testid="blocked-date-input" className={input} />
          <button onClick={addBlocked} data-testid="blocked-date-add" className="rounded-full border border-white/10 px-4 py-2 text-sm text-slate-200 hover:border-cyan-400/50">Block date</button>
        </div>
        <button onClick={save} data-testid="availability-save-btn" className="mt-6 inline-flex items-center gap-2 rounded-full bg-cyan-400 px-5 py-2 text-sm font-semibold text-[#07090E] hover:bg-cyan-300"><Save className="h-4 w-4" />Save availability</button>
      </div>
      <ServicesEditor appId={appId} services={data.services} reload={load} />
    </div>
  );
};

export const BookingsPanel = ({ appId }) => {
  const [when, setWhen] = useState("upcoming");
  const [data, setData] = useState({ items: [] });
  const [cancelling, setCancelling] = useState(null);
  const load = useCallback(() => api.get(`/manage/apps/${appId}/booking/bookings`, { params: { when } }).then((r) => setData(r.data)).catch(err), [appId, when]);
  useEffect(() => { load(); }, [load]);
  const cancel = async () => {
    try { await api.post(`/manage/apps/${appId}/booking/bookings/${cancelling.id}/cancel`); toast.success("Booking cancelled — the visitor has been emailed"); setCancelling(null); load(); }
    catch (x) { err(x); }
  };
  const tab = (k, label) => <button onClick={() => setWhen(k)} data-testid={`bookings-filter-${k}`} className={`rounded-full border px-3 py-1 text-xs ${when === k ? "border-cyan-400/60 bg-cyan-400/10 text-cyan-200" : "border-white/10 text-slate-300"}`}>{label}</button>;
  return (
    <div data-testid="bookings-panel">
      <div className="mb-3 flex flex-wrap items-center gap-2">{tab("upcoming", "Upcoming")}{tab("past", "Past")}{tab("cancelled", "Cancelled")}
        <span data-testid="bookings-timezone" className="ml-auto text-xs text-slate-500">Times in {data.timezone_label}</span></div>
      <div className="overflow-x-auto rounded-2xl border border-white/5 bg-[#0B0F18]">
        <table className="w-full min-w-[640px] text-left text-sm">
          <thead className="border-b border-white/5 text-xs uppercase tracking-wider text-slate-500"><tr><th className="px-4 py-3 font-medium">When</th><th className="px-4 py-3 font-medium">Service</th><th className="px-4 py-3 font-medium">Visitor</th><th className="px-4 py-3 font-medium">Status</th><th className="px-4 py-3" /></tr></thead>
          <tbody>
            {data.items.map((b) => (
              <tr key={b.id} data-testid={`booking-row-${b.id}`} className="border-b border-white/5 text-slate-300 last:border-0">
                <td className="px-4 py-3 text-white">{b.start_label}</td>
                <td className="px-4 py-3">{b.service_name} <span className="text-slate-500">· {b.duration} min</span></td>
                <td className="px-4 py-3"><div className="text-white">{b.name}</div><div className="text-xs text-slate-500">{b.email}{b.phone ? ` · ${b.phone}` : ""}</div>{b.notes && <div className="mt-1 line-clamp-2 text-xs text-slate-400">{b.notes}</div>}</td>
                <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-xs ${b.status === "Confirmed" ? "bg-emerald-400/10 text-emerald-300" : "bg-red-500/10 text-red-300"}`}>{b.status}</span></td>
                <td className="px-4 py-3 text-right">{b.status === "Confirmed" && when === "upcoming" && (
                  <button onClick={() => setCancelling(b)} data-testid={`booking-cancel-${b.id}`} className="inline-flex items-center gap-1 rounded-full border border-red-400/30 px-3 py-1 text-xs text-red-300 hover:bg-red-500/10"><CalendarX2 className="h-3.5 w-3.5" />Cancel</button>)}</td>
              </tr>
            ))}
            {!data.items.length && <tr><td colSpan={5} data-testid="bookings-empty" className="px-4 py-10 text-center text-slate-500">No {when} bookings.</td></tr>}
          </tbody>
        </table>
      </div>
      <ConfirmDelete target={cancelling} title={cancelling ? `Cancel ${cancelling.name}'s booking?` : ""} description={cancelling ? `${cancelling.service_name} on ${cancelling.start_label}. The time becomes free again and the visitor is emailed "Your booking was cancelled".` : ""} action="Cancel booking" onCancel={() => setCancelling(null)} onConfirm={cancel} />
    </div>
  );
};
